import { createFileRoute } from "@tanstack/react-router";
import { useFleetSidebar } from "@/features/brewcontrol/use-fleet-sidebar";
import { toast } from "sonner";
import type {
  BrewcontrolConfigVersionId,
  EquipmentId,
  EquipmentModelId,
} from "@brewbase/db/types";
import { trpc } from "@/hooks/trpc";
import { useCan } from "@/hooks/use-operator";
import { WiringEditorSession } from "@/features/brewcontrol/wiring-editor-session";
import { emptyWiring } from "@/features/brewcontrol/wiring-editor-model";

export const Route = createFileRoute("/brewcontrol/wirings/$modelId/")({
  component: ModelWiringRoute,
});

function ModelWiringRoute() {
  useFleetSidebar();
  const { modelId } = Route.useParams();
  return <ModelWiring key={modelId} />;
}

/**
 * A rig type's wiring: one document, one lineage, and the vessels running it.
 *
 * The wiring lives here rather than on a node because it is the most reusable
 * object in the system and a node is the least reusable one. Four identical
 * tanks share this document verbatim; what differs between them is which
 * device signal each one exposes under which alias, and that lives in each
 * node's devices document.
 *
 * Saving is not applying. A new version is authored here and reaches a tank
 * only when someone points that tank at it — every apply is a deliberate,
 * per-vessel act, which is what you want when the thing being changed is
 * driving a live fermentation.
 *
 * The page is the editor: the route owns the data and the mutations, the
 * session owns the draft and survives polling — a version someone else saves
 * meanwhile marks the draft stale rather than replacing it.
 */
function ModelWiring() {
  const { modelId } = Route.useParams();
  const equipmentModelId = Number(modelId) as EquipmentModelId;
  const canEdit = useCan("brewcontrol-nodes:manage");
  const utils = trpc.useUtils();

  const lineage = trpc.brewcontrolConfig.wiringLineage.useQuery(
    { equipmentModelId },
    { refetchInterval: 5_000 },
  );
  const authoring = trpc.brewcontrolConfig.wiringAuthoring.useQuery({
    equipmentModelId,
  });
  const onError = (e: { message: string }) => toast.error(e.message);

  const save = trpc.brewcontrolConfig.saveWiring.useMutation({
    onSuccess: async () => {
      toast.success(
        "Saved a new version. Apply it to a vessel to put it in use.",
      );
      await utils.brewcontrolConfig.invalidate();
    },
    onError,
  });
  const apply = trpc.brewcontrolConfig.setVesselWiring.useMutation({
    onSuccess: () => {
      toast.success("Applied.");
      void utils.brewcontrolConfig.invalidate();
    },
    onError,
  });
  const applyTo = (
    equipmentId: EquipmentId,
    versionId: BrewcontrolConfigVersionId | null,
  ) => {
    apply.mutate(
      { equipmentId, versionId },
      {
        onError: (error) => {
          // An in-run change is sometimes exactly the point, so the refusal is a
          // confirmation rather than a wall.
          if (error.data?.code !== "PRECONDITION_FAILED") return onError(error);
          if (window.confirm(`${error.message} Apply now?`)) {
            apply.mutate({ equipmentId, versionId, confirmedActiveRun: true });
          }
        },
      },
    );
  };

  // The empty document is named after the model, so both queries gate the mount.
  if (!lineage.data || (!authoring.data && !authoring.error)) {
    return (
      <div className="px-6 py-6 text-sm text-olive-11">
        {lineage.error?.message ?? "Loading…"}
      </div>
    );
  }
  if (authoring.error) {
    return (
      <div className="px-6 py-6 text-sm text-red-700">
        {authoring.error.message}
      </div>
    );
  }
  const latest = lineage.data.versions[0];
  return (
    <WiringEditorSession
      equipmentModelId={equipmentModelId}
      authoring={authoring.data}
      lineage={lineage.data}
      initialContent={
        latest?.content ?? emptyWiring(authoring.data?.modelName ?? "")
      }
      canEdit={canEdit}
      pending={save.isPending}
      onSave={(content, expectedVersionId) =>
        save.mutateAsync({ equipmentModelId, content, expectedVersionId })
      }
      applying={apply.isPending}
      onApply={applyTo}
    />
  );
}
